import {
	Alert,
	AlertDescription,
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Skeleton,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ancatag/at-design";
import { Key, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import { CreateKeyModal } from "../components/api-keys/CreateKeyModal";
import { CreateProjectDialog } from "../components/projects/CreateProjectDialog";
import { ProjectEmptyState } from "../components/projects/ProjectEmptyState";
import { useAuth } from "../context/AuthContext";
import { useProjectContext } from "../context/ProjectContext";
import {
	useApiKeys,
	useRevokeApiKey,
	useToggleApiKey,
} from "../hooks/useApiKeys";
import type { GenerationType } from "../types/api-keys";

const generationTypeColors: Record<GenerationType, string> = {
	text: "bg-blue-500/10 text-blue-400",
	image: "bg-purple-500/10 text-purple-400",
	video: "bg-green-500/10 text-green-400",
	audio: "bg-orange-500/10 text-orange-400",
};

export function ApiKeys(): React.JSX.Element {
	const {
		selectedProject,
		isLoading: projectLoading,
		refreshProjects,
	} = useProjectContext();
	const { data: keys, isLoading, isError, error } = useApiKeys();
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const revokeKey = useRevokeApiKey();
	const toggleKey = useToggleApiKey();
	const [createProjectOpen, setCreateProjectOpen] = useState(false);

	const handleToggleActive = async (
		keyId: string,
		isActive: boolean,
	): Promise<void> => {
		await toggleKey.mutateAsync({ keyId, isActive });
	};

	const handleRevoke = async (keyId: string): Promise<void> => {
		if (
			confirm(
				"Are you sure you want to revoke this API key? This action cannot be undone.",
			)
		) {
			await revokeKey.mutateAsync(keyId);
		}
	};

	const formatDate = (dateString?: string): string => {
		if (!dateString) return "Never";
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const formatLastUsed = (dateString?: string): string => {
		if (!dateString) return "Never used";
		const date = new Date(dateString);
		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffMins = Math.floor(diffMs / 60000);
		const diffHours = Math.floor(diffMs / 3600000);
		const diffDays = Math.floor(diffMs / 86400000);

		if (diffMins < 1) return "Just now";
		if (diffMins < 60) return `${diffMins}m ago`;
		if (diffHours < 24) return `${diffHours}h ago`;
		if (diffDays < 7) return `${diffDays}d ago`;
		return formatDate(dateString);
	};

	if (projectLoading) {
		return <ApiKeysSkeleton />;
	}

	const { user } = useAuth();
	const isSuperAdmin = user?.role === "superAdmin";

	if (!selectedProject && !isSuperAdmin) {
		return (
			<div className="space-y-6">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-3xl font-bold text-foreground">API Keys</h1>
						<p className="text-muted-foreground mt-2">
							Playground uses an internal sandbox token—API keys are not
							required for playground testing. Create a project only if you need
							keys for production.
						</p>
					</div>
				</div>
				<Alert>
					<AlertDescription>
						For playground testing, no API key is needed. For production or
						non-sandbox use, create a project and generate API keys here.
					</AlertDescription>
				</Alert>
				<ProjectEmptyState
					onCreateProject={() => setCreateProjectOpen(true)}
					onRefresh={refreshProjects}
				/>
				<CreateProjectDialog
					open={createProjectOpen}
					onOpenChange={setCreateProjectOpen}
				/>
			</div>
		);
	}

	if (isLoading) {
		return <ApiKeysSkeleton />;
	}

	if (isError) {
		const errorMessage =
			error instanceof Error ? error.message : "Unknown error";
		const isAccessError = errorMessage.includes("do not have access");

		return (
			<div className="space-y-6">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-3xl font-bold text-foreground">API Keys</h1>
						<p className="text-muted-foreground mt-2">
							Manage your API keys for accessing Nova AI services
						</p>
					</div>
				</div>
				<Card className="border-destructive/50 bg-destructive/10">
					<CardContent className="pt-6">
						<p className="text-destructive mb-4">
							Failed to load API keys: {errorMessage}
						</p>
						{isAccessError && (
							<div className="space-y-2">
								<p className="text-sm text-muted-foreground">
									The selected project may not belong to your account. Try
									refreshing to reload your projects.
								</p>
								<Button
									variant="outline"
									size="sm"
									onClick={() => {
										localStorage.removeItem("nova:lastProjectId");
										refreshProjects();
									}}
								>
									Refresh Projects
								</Button>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				<div>
					<h1 className="text-2xl md:text-3xl font-bold text-foreground">
						API Keys
					</h1>
					<p className="text-muted-foreground mt-2 text-sm md:text-base">
						Manage your API keys for accessing Nova AI services. Playground
						testing does not require API keys (uses sandbox token).
					</p>
				</div>
				<Button
					onClick={() => setIsCreateModalOpen(true)}
					className="w-full md:w-auto"
				>
					<Plus className="w-4 h-4 mr-2" />
					Create New Key
				</Button>
			</div>

			{/* Stats Cards */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground">
							Total Keys
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{keys?.length || 0}</div>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground">
							Active Keys
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-green-600">
							{keys?.filter((k) => k.isActive).length || 0}
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground">
							Total Requests
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{(
								keys?.reduce((sum, k) => sum + (k.totalRequests ?? 0), 0) ?? 0
							).toLocaleString()}
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Keys Table */}
			{keys && keys.length > 0 ? (
				<Card>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Permissions</TableHead>
								<TableHead>Usage</TableHead>
								<TableHead>Last Used</TableHead>
								<TableHead>Status</TableHead>
								<TableHead className="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{keys.map((apiKey) => (
								<TableRow key={apiKey.id}>
									<TableCell>
										<Link href={`/api-keys/${apiKey.id}`}>
											<div className="cursor-pointer hover:text-primary">
												<div className="font-medium">{apiKey.name}</div>
												{apiKey.description && (
													<div className="text-xs text-muted-foreground">
														{apiKey.description}
													</div>
												)}
											</div>
										</Link>
									</TableCell>
									<TableCell>
										<div className="flex flex-wrap gap-1">
											{apiKey.allowedGenerationTypes?.map((type) => (
												<Badge
													key={type}
													variant="secondary"
													className={generationTypeColors[type]}
												>
													{type}
												</Badge>
											)) || (
												<Badge variant="secondary" className="bg-gray-500/10">
													All
												</Badge>
											)}
										</div>
									</TableCell>
									<TableCell>
										<div className="text-sm">
											{(apiKey.totalRequests ?? 0).toLocaleString()} requests
										</div>
										<div className="text-xs text-muted-foreground">
											Limit:{" "}
											{apiKey.dailyRequestLimit
												? apiKey.dailyRequestLimit.toLocaleString()
												: "∞"}
											/day
										</div>
									</TableCell>
									<TableCell>
										<span className="text-sm text-muted-foreground">
											{formatLastUsed(apiKey.lastUsedAt)}
										</span>
									</TableCell>
									<TableCell>
										<div className="flex items-center gap-2">
											<Switch
												checked={apiKey.isActive}
												onCheckedChange={(checked) =>
													handleToggleActive(apiKey.id, checked)
												}
											/>
											<span
												className={`text-xs ${
													apiKey.isActive
														? "text-green-600"
														: "text-muted-foreground"
												}`}
											>
												{apiKey.isActive ? "Active" : "Inactive"}
											</span>
										</div>
									</TableCell>
									<TableCell className="text-right">
										<Button
											variant="ghost"
											size="icon"
											className="h-10 w-10 text-destructive hover:text-destructive hover:bg-destructive/10"
											onClick={() => handleRevoke(apiKey.id)}
											aria-label="Revoke key"
										>
											<Trash2 className="h-5 w-5" />
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Card>
			) : (
				<Card>
					<CardContent className="flex flex-col items-center justify-center py-12">
						<Key className="h-12 w-12 text-muted-foreground mb-4" />
						<h3 className="text-lg font-semibold mb-2">No API Keys</h3>
						<p className="text-muted-foreground text-center mb-4">
							Create your first API key to start using Nova AI services
						</p>
						<Button onClick={() => setIsCreateModalOpen(true)}>
							<Plus className="w-4 h-4 mr-2" />
							Create Your First Key
						</Button>
					</CardContent>
				</Card>
			)}

			{/* Create Key Modal */}
			<CreateKeyModal
				open={isCreateModalOpen}
				onOpenChange={setIsCreateModalOpen}
			/>
			<CreateProjectDialog
				open={createProjectOpen}
				onOpenChange={setCreateProjectOpen}
			/>
		</div>
	);
}

function ApiKeysSkeleton(): React.JSX.Element {
	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<Skeleton className="h-8 w-32 mb-2" />
					<Skeleton className="h-4 w-64" />
				</div>
				<Skeleton className="h-10 w-36" />
			</div>
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				{[1, 2, 3].map((i) => (
					<Card key={i}>
						<CardHeader className="pb-2">
							<Skeleton className="h-4 w-20" />
						</CardHeader>
						<CardContent>
							<Skeleton className="h-8 w-12" />
						</CardContent>
					</Card>
				))}
			</div>
			<Card>
				<div className="p-4">
					{[1, 2, 3].map((i) => (
						<div
							key={i}
							className="flex items-center gap-4 py-4 border-b last:border-0"
						>
							<Skeleton className="h-4 w-32" />
							<Skeleton className="h-4 w-24" />
							<Skeleton className="h-4 w-20" />
							<Skeleton className="h-4 w-16" />
							<Skeleton className="h-4 w-16" />
						</div>
					))}
				</div>
			</Card>
		</div>
	);
}
